// src/pages/DeleteAccount.js
import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebase.utils';

const DeleteAccount = () => {
    const [email, setEmail] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!email || !email.trim()) {
            setError('Please enter your email address');
            setLoading(false);
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setError('Please enter a valid email address');
            setLoading(false);
            return;
        }

        try {
            await addDoc(collection(db, 'delete-requests'), {
                email: email.trim().toLowerCase(),
                timestamp: serverTimestamp()
            });
            setSubmitted(true);
            setEmail('');
        } catch (err) {
            console.error('Error submitting delete request:', err);
            setError('Failed to submit request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex flex-col min-h-screen bg-gray-100">
            <div className="flex justify-center mt-8">
                <img 
                    src="https://infaque.com/wp-content/uploads/2024/01/Infaque-Primary-Logo-RGB-Canary-768x231.png" 
                    alt="Infaque POS Logo" 
                    className="w-40 mb-8" 
                />
            </div>
            <div className="flex flex-col items-center justify-center flex-grow px-4 py-8">
                <div className="w-full max-w-2xl bg-white rounded-lg shadow-md p-6 mb-6">
                    <h1 className="text-3xl font-bold mb-2 text-center">Delete Your Infaque POS Account</h1>
                    <p className="text-center text-gray-600 mb-6">
                        Request to delete your account and associated data from Infaque POS
                    </p>

                    {/* Steps to Request Account Deletion */}
                    <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                        <h2 className="text-xl font-semibold mb-3 text-blue-900">How to Request Account Deletion</h2>
                        <ol className="list-decimal list-inside space-y-2 text-gray-700">
                            <li>Enter your email address associated with your Infaque POS account in the form below</li>
                            <li>Click the "Submit Request" button to submit your deletion request</li>
                            <li>Our team will review and process your request within 30 days</li>
                            <li>You will receive a confirmation email once your account has been deleted</li>
                        </ol>
                    </div>

                    {/* Data Deletion Information */}
                    <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded">
                        <h2 className="text-xl font-semibold mb-3">What Data Will Be Deleted</h2>
                        <p className="text-gray-700 mb-2">When you request account deletion, the following data will be permanently deleted:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 mb-4">
                            <li>Your user account information (email, name, role)</li>
                            <li>Your user logs and activity history</li>
                            <li>Your tenant association and permissions</li>
                            <li>All personal information stored in your account profile</li>
                        </ul>
                        
                        <h3 className="text-lg font-semibold mb-2 mt-4">What Data Will Be Retained</h3>
                        <p className="text-gray-700 mb-2">The following data will be retained for legal and business purposes:</p>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 mb-2">
                            <li>Transaction records and payment history (required for financial compliance)</li>
                            <li>Completed payment data associated with fundraising targets</li>
                            <li>Tenant and organizational data (if you were part of a tenant organization)</li>
                        </ul>
                        
                        <p className="text-sm text-gray-600 mt-3">
                            <strong>Retention Period:</strong> Financial transaction records are retained for 7 years as required by law. 
                            Other retained data may be kept for up to 90 days after account deletion for audit and compliance purposes.
                        </p>
                    </div>

                    {/* Form */}
                    {submitted ? (
                        <div className="text-center p-4 bg-green-50 border border-green-200 rounded">
                            <p className="text-green-700 mb-4 font-semibold">
                                Your account deletion request has been submitted successfully. 
                                We will process it within 30 days and send you a confirmation email.
                            </p>
                            <button
                                onClick={() => setSubmitted(false)}
                                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                                Submit Another Request
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="border-t pt-6">
                            <div className="mb-4">
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                    Email Address
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email address"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Enter the email address associated with your Infaque POS account
                                </p>
                            </div>
                            {error && (
                                <div className="mb-4 text-red-600 text-sm bg-red-50 p-3 rounded">{error}</div>
                            )}
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-semibold"
                            >
                                {loading ? 'Submitting...' : 'Submit Deletion Request'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeleteAccount;

